import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { PermissionMap } from '../types/permissions';
import { useTenant } from './tenant-context';
import { useUserService } from '../services/userService';


interface PermissionContextType {
  permissions: PermissionMap | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined);

export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (!context) {
    // Return safe defaults when provider is not available (e.g., on auth-loading page)
    return {
      permissions: null,
      loading: true,
      error: null,
      refetch: async () => {},
    };
  }
  return context;
};

interface PermissionProviderProps {
  children: ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const { accounts } = useMsal();
  const { selectedTenantId, userSession } = useTenant();
  const { getUserPermissions } = useUserService();
  
  const [permissions, setPermissions] = useState<PermissionMap | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const accountsLength = accounts.length;

  const loadPermissions = React.useCallback(async () => {
    if (accountsLength === 0) {
      setLoading(false);
      return;
    }

    // Don't start loading until we have a user session
    if (!userSession) {
      setLoading(true);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Determine which tenantId to pass to API:
      // - '__GLOBAL_ADMIN__' selected: pass undefined (global admin permissions)
      // - Regular tenant selected: pass the tenantId
      // - Nothing selected for regular user: wait for selection
      const tenantIdForApi = selectedTenantId === '__GLOBAL_ADMIN__' ? undefined : selectedTenantId;
      
      // Regular users need a tenant selected
      if (!userSession.isGlobalAdmin && !selectedTenantId) {
        setPermissions(null);
        setLoading(false);
        return;
      }
      
      // Global admins need a selection (either Global Admin or a specific tenant)
      if (userSession.isGlobalAdmin && !selectedTenantId) {
        setPermissions(null);
        setLoading(false);
        return;
      }

      // Call API to get permissions
      // Backend determines permissions based on:
      // - If no tenantId: returns global admin permissions
      // - If tenantId present: returns tenant-specific permissions (Tenant Active, User Active, Entitlements, Role)
      const fetchedPermissions = await getUserPermissions(tenantIdForApi);

      setPermissions(fetchedPermissions);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err : new Error("Unknown error loading permissions");

      setError(errorMessage);
      setPermissions(null);
    } finally {
      setLoading(false);
    }
  }, [accountsLength, userSession, selectedTenantId, getUserPermissions]);

  // Fetch permissions on mount or when accounts, userSession, or selectedTenantId change
  useEffect(() => {
    loadPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountsLength, userSession, selectedTenantId]);

  const value = React.useMemo<PermissionContextType>(() => ({
    permissions,
    loading,
    error,
    refetch: loadPermissions,
  }), [permissions, loading, error, loadPermissions]);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};
